import { useEffect, useState } from 'react';
import { decode } from 'blurhash';
import { mediaUrl, type MediaVariant } from './types';

/**
 * Phase 4 task list: `<picture>` with AVIF/WebP/JPEG sources, correct `sizes`,
 * `loading="lazy"`, `decoding="async"`, explicit width/height, and a blurhash placeholder.
 * Explicit `width`/`height` (not just the wrapper's `aspect-ratio`) is what actually
 * satisfies the Phase 4 DoD's "zero cumulative layout shift" — the browser reserves the
 * box before either the placeholder or the real image has loaded.
 */
export interface ResponsiveImageProps {
  variants: MediaVariant[];
  /** Original file's storage path, used only if no JPEG variant exists (shouldn't happen
   * for anything the Phase 4 upload pipeline produced, but keeps this component honest
   * about media rows created some other way). */
  fallbackStoragePath: string;
  width: number;
  height: number;
  alt: string;
  blurhash?: string | null;
  sizes?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}

function srcSet(variants: MediaVariant[], format: MediaVariant['format']): string | undefined {
  const matches = variants
    .filter((v) => v.format === format)
    .sort((a, b) => a.width - b.width)
    .map((v) => `${mediaUrl(v.storagePath)} ${v.width}w`);
  return matches.length > 0 ? matches.join(', ') : undefined;
}

/** Client-only by design: decoding onto a `<canvas>` needs the DOM, and a plain background
 * colour is a perfectly fine pre-hydration state for something below the fold. */
function useBlurhashDataUrl(blurhash: string | null | undefined): string | undefined {
  const [dataUrl, setDataUrl] = useState<string>();

  useEffect(() => {
    if (!blurhash) return;
    try {
      const pixels = decode(blurhash, 32, 32);
      // eslint-disable-next-line no-restricted-globals -- inside useEffect: client-only.
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const imageData = ctx.createImageData(32, 32);
      imageData.data.set(pixels);
      ctx.putImageData(imageData, 0, 0);
      setDataUrl(canvas.toDataURL());
    } catch {
      // A malformed blurhash loses the placeholder, not the image.
    }
  }, [blurhash]);

  return dataUrl;
}

export function ResponsiveImage({
  variants,
  fallbackStoragePath,
  width,
  height,
  alt,
  blurhash,
  sizes = '100vw',
  className,
  loading = 'lazy',
}: ResponsiveImageProps) {
  const [loaded, setLoaded] = useState(false);
  const placeholderUrl = useBlurhashDataUrl(blurhash);
  const jpegVariant = variants.find((v) => v.format === 'jpeg');
  const fallbackSrc = mediaUrl(jpegVariant?.storagePath ?? fallbackStoragePath);

  return (
    <picture
      className={className}
      style={{
        display: 'block',
        position: 'relative',
        aspectRatio: `${width} / ${height}`,
        backgroundColor: 'var(--soft, #f2f3f5)',
        backgroundImage: !loaded && placeholderUrl ? `url(${placeholderUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden',
      }}
    >
      <source type="image/avif" srcSet={srcSet(variants, 'avif')} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(variants, 'webp')} sizes={sizes} />
      <img
        src={fallbackSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        onLoad={() => setLoaded(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </picture>
  );
}
